/* eslint-disable no-console */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Divider, Heading, Icon, List, Menu, Modal, TextButton } from '@zillow/constellation-10';
import { IconNotificationOutline } from '@zillow/constellation-icons';

import {
  RentalManagerNotificationStatus,
  type UserNotificationsAllNotificationsNotificationWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerUserNotificationsWebApiHandler_allNotifications,
  useRentalManagerUserNotificationsWebApiHandler_markAllAsRead,
  useRentalManagerUserNotificationsWebApiHandler_markAsRead,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import NotificationMenuItem from './NotificationMenuItem';
import DropdownMenu from '../DropdownMenu';

import Constants from '../../constants/notification';

import { notificationCircleStyles } from './styles';

import type { NotificationMenuItemVariant } from './NotificationMenuItem';

export type Props = {
  brand: string;
  webSocketInstance?: unknown;
  useLeftNav?: boolean;
  isOpen?: boolean;
  onHasNewNotificationsChange?: (hasNew: boolean) => void;
  onClose?: () => void;
};

const NOTIFICATIONS_PAGE_SIZE = 10;

const NotificationCenter = ({
  brand,
  webSocketInstance,
  useLeftNav = false,
  isOpen = false,
  onHasNewNotificationsChange,
  onClose,
}: Props) => {
  const [stateValues, setStateValues] = useState<{
    notifications: Array<UserNotificationsAllNotificationsNotificationWebDto>;
    isPopupOpen: boolean;
    hasError: boolean;
    startIndex: number;
    showPagination: boolean;
    showMarkAllAsRead: boolean;
    hasNewNotifications: boolean;
  }>({
    notifications: [],
    isPopupOpen: false,
    hasError: false,
    startIndex: 0,
    showPagination: false,
    showMarkAllAsRead: false,
    hasNewNotifications: false,
  });
  const [showModal, setShowModal] = useState(false);
  const [ws, setWS] = useState<WebSocket | null>(null);
  const [paginationParams, setPaginationParams] = useState<{ start: number; limit: number }>({
    start: 0,
    limit: NOTIFICATIONS_PAGE_SIZE,
  });

  const { notifications, showPagination, hasError, showMarkAllAsRead, hasNewNotifications } = stateValues;

  // React Query hooks
  const {
    data: notificationsData,
    isError: notificationsError,
    refetch: refetchNotifications,
  } = useRentalManagerUserNotificationsWebApiHandler_allNotifications(
    {
      start: paginationParams.start,
      limit: paginationParams.limit,
      includeHasNewNotificationsForNotificationCenter: true,
    },
    {
      enabled: true,
    },
  );

  const markAsReadMutation = useRentalManagerUserNotificationsWebApiHandler_markAsRead({
    onSuccess: (_data, variables) => {
      const updatedNotifications = notifications.map(
        (notificationFromState: UserNotificationsAllNotificationsNotificationWebDto) => {
          if (String(variables.getParams.notificationId) === notificationFromState.notificationId) {
            return { ...notificationFromState, status: RentalManagerNotificationStatus.READ };
          }
          return notificationFromState;
        },
      );
      setStateValues((prev) => ({ ...prev, notifications: updatedNotifications }));
    },
  });

  const markAllAsReadMutation = useRentalManagerUserNotificationsWebApiHandler_markAllAsRead({
    onSuccess: () => {
      const updatedNotifications = notifications.map((notification) => ({
        ...notification,
        status: RentalManagerNotificationStatus.READ,
      }));
      setStateValues((prev) => ({
        ...prev,
        hasNewNotifications: false,
        notifications: updatedNotifications,
        showMarkAllAsRead: false,
      }));
    },
  });

  // Process notifications data from React Query
  useEffect(() => {
    if (notificationsData) {
      const {
        notifications: responseNotifications,
        hasNewNotificationsForNotificationCenter = false,
        pager,
      } = notificationsData;

      const showPaginationBool = pager.currentPage !== pager.numPages;
      const showMarkAllAsReadBool = checkIfUnreadNotifications(responseNotifications);

      // If this is a pagination request (startIndex > 0), append notifications
      if (paginationParams.start > 0) {
        const newNotifications = [...notifications, ...responseNotifications];
        setStateValues((prev) => ({
          ...prev,
          notifications: newNotifications,
          startIndex: paginationParams.start + NOTIFICATIONS_PAGE_SIZE,
          showPagination: showPaginationBool,
          showMarkAllAsRead: prev.showMarkAllAsRead || showMarkAllAsReadBool,
          isFirstTimeLoad: false,
          hasNewNotifications: hasNewNotificationsForNotificationCenter,
        }));
      } else {
        setStateValues((prev) => ({
          ...prev,
          notifications: responseNotifications,
          startIndex: NOTIFICATIONS_PAGE_SIZE,
          showPagination: showPaginationBool,
          showMarkAllAsRead: responseNotifications.length > 0 && showMarkAllAsReadBool,
          isFirstTimeLoad: false,
          hasNewNotifications: hasNewNotificationsForNotificationCenter,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsData]);

  // Handle query errors
  useEffect(() => {
    if (notificationsError) {
      setStateValues((prev) => ({
        ...prev,
        hasError: true,
        showPagination: false,
      }));
    }
  }, [notificationsError]);

  // Notify parent when hasNewNotifications changes
  useEffect(() => {
    onHasNewNotificationsChange?.(hasNewNotifications);
  }, [hasNewNotifications, onHasNewNotificationsChange]);

  useEffect(() => {
    setupWSConnection();

    return () => {
      removeWSListeners();
      closeWS();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ws) {
      removeWSListeners();
      closeWS();

      return;
    }

    registerWSListeners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws]);

  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const refreshNotifications = useCallback(() => {
    // Reset pagination and refetch
    setPaginationParams({ start: 0, limit: NOTIFICATIONS_PAGE_SIZE });
    refetchNotifications();
  }, [refetchNotifications]);

  const setupWSConnection = () => {
    let newWS: WebSocket | null = null;

    // if webSocketInstance is passed use it. otherwise create new instance
    if (!webSocketInstance) {
      const wsUrl = Constants.WS_BRAND_CONNECTIONS[brand as keyof typeof Constants.WS_BRAND_CONNECTIONS];
      if (wsUrl) {
        newWS = new WebSocket(wsUrl);
      }
    } else {
      const wsInstance = webSocketInstance as { subscriptionClient?: { client: WebSocket } };
      newWS = wsInstance.subscriptionClient ? wsInstance.subscriptionClient.client : (webSocketInstance as WebSocket);
    }

    console.log('setup websocket', newWS);
    setWS(newWS);
  };

  const registerWSListeners = () => {
    if (!ws) {
      return;
    }

    ws.addEventListener('message', handleWSMessage);
    ws.addEventListener('close', handleWSClose);
    ws.addEventListener('error', handleWSError);
  };

  const removeWSListeners = () => {
    if (!ws) {
      return;
    }

    ws.removeEventListener('message', handleWSMessage);
    ws.removeEventListener('close', handleWSClose);
    ws.removeEventListener('error', handleWSError);
  };

  const closeWS = () => {
    if (!ws) {
      return;
    }

    ws.close();
  };

  const handleMenuItemClick = (notification: UserNotificationsAllNotificationsNotificationWebDto) => {
    handleMarkRead(notification);
    closeDropdown();
  };

  const checkIfUnreadNotifications = (
    notificationsFromState: Array<UserNotificationsAllNotificationsNotificationWebDto>,
  ) => {
    return notificationsFromState.filter((notification) => notification.status === 'UNREAD').length > 0;
  };

  const closeDropdown = () => {
    closeBtnRef?.current?.click();
  };

  const handleWSMessage = (event: unknown) => {
    console.log('>> websocket message received.', event);
    refreshNotifications();
  };

  const handleWSClose = (event: unknown) => {
    console.log('>> websocket closed successfully.', event);
    setWS(null);
  };

  const handleWSError = (event: unknown) => {
    console.log('>> websocket connection failed', event);
  };

  const handleNotificationsExpand = () => {
    // Clear the new notifications indicator when user expands the notification center
    setStateValues((prev) => ({
      ...prev,
      hasNewNotifications: false,
    }));
  };

  const handleMarkRead = ({ notificationId }: UserNotificationsAllNotificationsNotificationWebDto) => {
    markAsReadMutation.mutate({
      getParams: { notificationId: Number(notificationId) },
    });
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleOpen = () => {
    handleNotificationsExpand();
  };

  const getMoreNotifications = (event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
    if (event) {
      event.stopPropagation();
    }

    // Update pagination params to fetch next page
    setPaginationParams((prev) => ({
      start: prev.start + NOTIFICATIONS_PAGE_SIZE,
      limit: NOTIFICATIONS_PAGE_SIZE,
    }));
  };

  const renderNotifications = (variant: NotificationMenuItemVariant = 'menu') => {
    if (notifications.length === 0) {
      if (variant === 'list') {
        return <List.Item css={{ opacity: 'action.active' }}>You do not have any notifications</List.Item>;
      }
      return (
        <Menu.Item disabled css={{ opacity: 'action.active' }}>
          <Menu.ItemLabel>You do not have any notifications</Menu.ItemLabel>
        </Menu.Item>
      );
    }

    return notifications.map((notificationItem: UserNotificationsAllNotificationsNotificationWebDto) => (
      <NotificationMenuItem
        key={`notificationItem-${notificationItem.notificationId}`}
        notificationItem={notificationItem}
        onClick={() => handleMenuItemClick(notificationItem)}
        variant={variant}
      />
    ));
  };

  const renderIcon = () => {
    if (useLeftNav === false) {
      return (
        <TextButton
          tone="neutral"
          icon={<IconNotificationOutline />}
          iconPosition="end"
          data-popper-placement="bottom"
          aria-label="View notifications"
          css={{ position: 'relative' }}
        >
          {hasNewNotifications ? <Box css={notificationCircleStyles} /> : null}
        </TextButton>
      );
    }

    // side nav layout
    return (
      <TextButton
        tone="neutral"
        data-popper-placement="bottom"
        aria-label="View notifications"
        tabIndex={-1}
        css={{ position: 'relative', marginRight: '200' }}
      >
        <Icon render={<IconNotificationOutline />} css={{ marginRight: '200' }} size="md" />
      </TextButton>
    );
  };

  const renderContent = (variant: NotificationMenuItemVariant = 'menu') => (
    <>
      {renderNotifications(variant)}
      {showPagination && (
        <TextButton
          onClick={getMoreNotifications}
          css={{ padding: variant === 'menu' ? '{spacing.300} {spacing.400}' : '{spacing.400} 0' }}
        >
          <TextButton.Label css={{ textStyle: 'body-xs' }}>Show more notifications</TextButton.Label>
        </TextButton>
      )}
    </>
  );

  const renderError = (variant: NotificationMenuItemVariant = 'menu') => {
    if (variant === 'list') {
      return <List.Item>There was an error in fetching the notifications</List.Item>;
    }
    return (
      <Menu.Item disabled css={{ opacity: 'action.active' }}>
        <Menu.ItemLabel>There was an error in fetching the notifications</Menu.ItemLabel>
      </Menu.Item>
    );
  };

  return (
    <Box data-testid="NotificationCenter">
      <Box css={{ display: { base: 'none', xl: 'block' } }}>
        <DropdownMenu
          title={renderIcon()}
          onOpen={handleOpen}
          useLeftNav={useLeftNav}
          pIsOpen={isOpen}
          onClose={onClose}
        >
          {hasError ? (
            <>{renderError()}</>
          ) : (
            <>
              <Box
                css={{
                  overflowY: 'auto',
                  maxHeight: '360px',
                }}
              >
                {renderContent()}
              </Box>
              {showMarkAllAsRead && (
                <>
                  <Divider />
                  <TextButton
                    css={{ margin: '{spacing.300} {spacing.400}', _hover: { textDecoration: 'none' } }}
                    onClick={handleMarkAllAsRead}
                  >
                    Mark all as read
                  </TextButton>
                </>
              )}
            </>
          )}
        </DropdownMenu>
      </Box>
      <Box css={{ display: { base: 'block', xl: 'none' } }}>
        <TextButton
          tone="neutral"
          aria-label="View notifications"
          icon={<IconNotificationOutline />}
          iconPosition="end"
          onClick={() => setShowModal(true)}
          css={{ position: 'relative' }}
        >
          {hasNewNotifications ? <Box css={notificationCircleStyles} /> : null}
        </TextButton>
        {showModal && (
          <Modal.Root open={showModal} onOpenChange={(open) => setShowModal(open)} size="fullScreen">
            <Modal.Portal css={{ display: { base: 'block', xl: 'none' } }}>
              <Modal.Backdrop>
                <Modal.Content data-testid="NotificationCenterModal">
                  <Modal.Header>
                    <Heading level={1}>Notifications</Heading>
                  </Modal.Header>
                  <Divider />
                  <Modal.Body>
                    <List.Root appearance="none">{hasError ? renderError('list') : renderContent('list')}</List.Root>
                  </Modal.Body>
                  <Divider />
                  {showMarkAllAsRead && (
                    <Modal.Footer>
                      <TextButton
                        css={{ marginBlock: '200', _hover: { textDecoration: 'none' } }}
                        onClick={handleMarkAllAsRead}
                      >
                        Mark all as read
                      </TextButton>
                    </Modal.Footer>
                  )}
                  <Modal.CloseButton />
                </Modal.Content>
              </Modal.Backdrop>
            </Modal.Portal>
          </Modal.Root>
        )}
      </Box>
    </Box>
  );
};

export default NotificationCenter;
